import React from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import type { SessionSummary } from './types'

function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000)
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  if (minutes === 0) return `${remainingSeconds}s`
  return `${minutes}m ${remainingSeconds}s`
}

function formatTokens(count: number): string {
  if (count < 1000) return `${count}`
  if (count < 1_000_000) return `${(count / 1000).toFixed(1)}K`
  return `${(count / 1_000_000).toFixed(2)}M`
}

export function SessionSummaryCard({ summary }: { summary: SessionSummary }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 3,
        border: '1px solid',
        borderColor: summary.is_error ? 'error.light' : 'success.light',
        backgroundColor: summary.is_error ? 'error.50' : 'success.50',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        {summary.is_error ? (
          <ErrorOutlineIcon color="error" fontSize="small" />
        ) : (
          <CheckCircleOutlineIcon color="success" fontSize="small" />
        )}
        <Typography variant="subtitle2" fontWeight={600}>
          Session {summary.is_error ? 'Failed' : 'Completed'}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {summary.duration_ms != null && (
          <Box>
            <Typography variant="caption" color="text.secondary">Duration</Typography>
            <Typography variant="body2" fontWeight={500}>{formatDuration(summary.duration_ms)}</Typography>
          </Box>
        )}
        {summary.cost_usd != null && (
          <Box>
            <Typography variant="caption" color="text.secondary">Cost</Typography>
            <Typography variant="body2" fontWeight={500}>${summary.cost_usd.toFixed(4)}</Typography>
          </Box>
        )}
        <Box>
          <Typography variant="caption" color="text.secondary">Input Tokens</Typography>
          <Typography variant="body2" fontWeight={500}>{formatTokens(summary.total_input_tokens)}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Output Tokens</Typography>
          <Typography variant="body2" fontWeight={500}>{formatTokens(summary.total_output_tokens)}</Typography>
        </Box>
      </Box>
      {summary.result && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary">Result</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {summary.result}
          </Typography>
        </Box>
      )}
    </Paper>
  )
}
